import { motion } from "framer-motion";
import { ArrowRight, Check, Briefcase, HeartHandshake } from "lucide-react";
import { siteContent } from "@/content/site";

const { audience } = siteContent;

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

export default function Audience() {
  const cards = [
    { ...audience.adultChild, icon: Briefcase, variant: "dark" },
    { ...audience.senior, icon: HeartHandshake, variant: "light" },
  ];

  return (
    <section
      id="for-you"
      data-testid="audience-section"
      className="relative py-20 lg:py-28 bg-[#E1EEEA]/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8A33D]">
            {audience.eyebrow}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A36]">
            {audience.headingLead}{" "}
            <em className="italic font-medium text-[#1F4E5F]">
              {audience.headingItalic}
            </em>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {cards.map((p, i) => (
            <Card key={i} data={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Card({ data }) {
  const isDark = data.variant === "dark";
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7 }}
      className={`relative rounded-3xl p-8 lg:p-10 border ${
        isDark
          ? "bg-[#1F4E5F] text-[#F4FAF8] border-[#1F4E5F]"
          : "bg-[#F4FAF8] text-[#0E2A36] border-[#C9DDD6]"
      } hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(15,56,32,0.10)] transition-all duration-300`}
      data-testid={`audience-card-${isDark ? "child" : "senior"}`}
    >
      <div
        className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${
          isDark ? "bg-[#E8A33D]/20 text-[#E8A33D]" : "bg-[#E1EEEA] text-[#1F4E5F]"
        }`}
      >
        <data.icon className="w-5 h-5" />
      </div>
      <span
        className={`text-xs font-bold uppercase tracking-[0.2em] ${
          isDark ? "text-[#E8A33D]" : "text-[#E8A33D]"
        }`}
      >
        {data.tag}
      </span>
      <h3 className="font-display mt-2 text-2xl lg:text-3xl font-semibold leading-tight">
        {data.title}
      </h3>
      <p
        className={`mt-3 text-[15px] leading-relaxed ${
          isDark ? "text-[#F4FAF8]/75" : "text-[#3D5560]"
        }`}
      >
        {data.body}
      </p>
      <ul className="mt-6 space-y-3">
        {data.bullets.map((b, i) => (
          <li key={i} className="flex gap-3">
            <span
              className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                isDark ? "bg-[#E8A33D]" : "bg-[#1F4E5F]"
              }`}
            >
              <Check className="w-3 h-3 text-white" />
            </span>
            <span
              className={`text-sm leading-relaxed ${
                isDark ? "text-[#F4FAF8]/90" : "text-[#3D5560]"
              }`}
            >
              {b}
            </span>
          </li>
        ))}
      </ul>
      <button
        data-testid={`audience-card-${isDark ? "child" : "senior"}-cta`}
        onClick={() => scrollTo("beta")}
        className={`mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all hover:gap-3 ${
          isDark
            ? "bg-[#E8A33D] hover:bg-[#C88A28] text-white"
            : "bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8]"
        }`}
      >
        {data.cta} <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
}
